import {
	ArrowLeftIcon,
	ArrowRightIcon,
	CalendarBlankIcon,
	CaretDownIcon,
	DotsSixVerticalIcon,
	ChatCircleIcon,
	MagnifyingGlassIcon,
} from '@phosphor-icons/react'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@journ/ui/dropdown-menu'
import { useCallback, useEffect, useEffectEvent, useMemo, useState } from 'react'
import type { CompanyWorkflow, Phase, Step, Transition, WorkItem } from '../domain/workflow'
import type { PhaseColumns } from '../application/group-work-items-by-step'
import { useWorkflows } from '../hooks/use-workflows'
import { useWorkItems } from '../hooks/use-work-items'
import { useKanbanColumns } from '../hooks/use-kanban-columns'
import { useFireTransition } from '../hooks/use-fire-transition'
import { useAllMembers } from '../hooks/use-all-members'
import { useWorkItemLifecycle } from '../hooks/use-work-item-lifecycle'
import '@xyflow/react/dist/style.css'

import {
	Kanban,
	KanbanBoard,
	KanbanColumn,
	KanbanColumnContent,
	KanbanColumnHandle,
	KanbanItem,
	KanbanItemHandle,
	KanbanOverlay,
	type KanbanMoveEvent,
} from '@/components/kanban'
import type { UniqueIdentifier } from '@dnd-kit/core'
import { Button } from '@journ/ui/button'
import { Input } from '@journ/ui/input'
import { useNavigate } from '@/libs/router'
import { useIntlayer } from 'react-intlayer'
import { useSelector } from '@/libs/state'
import { useDateFormat } from '@/features/preferences/hooks/use-date-format'
import { dueUrgency, stepAgeDays } from '../domain/work-item-card'
import { canTransitionStatus } from '../domain/work-item-status'
import { phaseDoneLedger, workflowArchivedLedger } from '../domain/phase-ledger'
import { visiblePhases } from '../domain/authorization'
import { getWorkItemActor } from '../hooks/use-work-item-actor'
import { boardNowMs$, creatingWorkItem$, kanbanColumnOrder$, members$, workItems$ } from '../store'
import { PriorityIndicator, AssigneeAvatar } from './work-item-card-visuals'
import { WorkItemCreateSheet } from './work-item-create-sheet'

const DONE_COLUMN_ID = '__done__'

type Props = {
	workflowId: string
}

// --- helpers (pure) ---

const findHandoffTransitions = (phase: Phase, stepId: string): Transition[] => {
	const step = phase.teamWorkflow.steps.find(s => s.id === stepId)
	if (!step) return []
	return step.transitions.filter(t => t.target.kind === 'phase_handoff')
}

const findStepTransitions = (phase: Phase, stepId: string): Transition[] => {
	const step = phase.teamWorkflow.steps.find(s => s.id === stepId)
	if (!step) return []
	return step.transitions.filter(
		t => t.trigger === 'manual' && (t.target.kind === 'step' || t.target.kind === 'transition'),
	)
}

const findPreviousStep = (phase: Phase, stepId: string): Step | null => {
	const steps = phase.teamWorkflow.steps
	const current = steps.find(s => s.id === stepId)
	if (!current) return null
	return steps.find(s => s.order === current.order - 1) ?? null
}

const useOpenWorkItem = () => {
	const navigate = useNavigate()
	return (item: WorkItem) =>
		navigate({
			to: '/tasks/$workflow-id/$work-item-id',
			params: { 'workflow-id': item.companyWorkflowId, 'work-item-id': item.id },
		})
}

// --- spawn list ---

type SpawnListProps = { parentId: string }

const SpawnList = ({ parentId }: SpawnListProps) => {
	const c = useIntlayer('work-item').detail
	const allWorkItems = useSelector(workItems$)
	const spawns = allWorkItems.filter(w => w.parentId === parentId && w.status !== 'archived')
	if (!spawns.length) return null

	const done = spawns.filter(s => s.status === 'completed').length

	return (
		<div className='mt-2.5 flex flex-col gap-1.5 border-t border-border pt-2.5'>
			<p className='text-xxs tabular-nums text-606060'>
				{done}/{spawns.length} {c.spawnsCountSuffix}
			</p>
			{spawns.map(s => (
				<div key={s.id} className='flex items-start gap-1.5'>
					<span
						className={`mt-0.5 size-3 shrink-0 border ${s.status === 'completed' ? 'bg-primary border-primary' : 'border-muted-foreground/40'}`}
					/>
					<span className={`text-xs leading-tight ${s.status === 'completed' ? 'line-through text-606060' : ''}`}>
						{s.title}
					</span>
				</div>
			))}
		</div>
	)
}

// --- card action bar ---

type ForwardOption = { id: string; label: string; kind: 'transition' | 'complete' }

type WorkItemCardActionsProps = {
	item: WorkItem
	step: Step
	phase: Phase
	onFireTransition: (item: WorkItem, transitionId: string) => Promise<unknown>
	onComplete: (item: WorkItem) => void
	onMoveBack: (item: WorkItem, stepId: string) => Promise<unknown>
}

const WorkItemCardActions = ({
	item,
	step,
	phase,
	onFireTransition,
	onComplete,
	onMoveBack,
}: WorkItemCardActionsProps) => {
	const c = useIntlayer('work-item').detail

	const handoffs = findHandoffTransitions(phase, item.currentStepId)
	const stepTransitions = findStepTransitions(phase, item.currentStepId)
	const prevStep = findPreviousStep(phase, item.currentStepId)

	const forwardOptions: ForwardOption[] = [
		...stepTransitions.map(t => ({ id: t.id, label: t.label, kind: 'transition' as const })),
		...handoffs.map(t => ({ id: t.id, label: t.label, kind: 'transition' as const })),
		...(step.canComplete && canTransitionStatus(item.status, 'completed')
			? [{ id: '__complete__', label: c.doneColumnTitle.value, kind: 'complete' as const }]
			: []),
	]

	if (!prevStep && forwardOptions.length === 0) {
		return null
	}

	const fireForward = (opt: ForwardOption) => {
		if (opt.kind === 'complete') {
			const defaultHandoff = handoffs.find(t => t.isDefault) ?? handoffs[0]
			if (defaultHandoff) onFireTransition(item, defaultHandoff.id)
			else onComplete(item)
		} else {
			onFireTransition(item, opt.id)
		}
	}

	return (
		<div className='mt-2.5 flex items-center gap-1'>
			{prevStep && (
				<button
					type='button'
					onClick={e => {
						e.stopPropagation()
						onMoveBack(item, prevStep.id)
					}}
					className='inline-flex items-center justify-center border border-border p-1 text-606060 transition-colors hover:border-primary/40 hover:text-primary'
					title={prevStep.name}
				>
					<ArrowLeftIcon className='size-3' />
				</button>
			)}
			{forwardOptions.length === 1 && forwardOptions[0] && (
				<button
					type='button'
					onClick={e => {
						e.stopPropagation()
						fireForward(forwardOptions[0]!)
					}}
					className='inline-flex items-center justify-center border border-border p-1 text-606060 transition-colors hover:border-primary/40 hover:text-primary'
					title={forwardOptions[0].label}
				>
					<ArrowRightIcon className='size-3' />
				</button>
			)}
			{forwardOptions.length > 1 && (
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<button
							type='button'
							onClick={e => e.stopPropagation()}
							className='inline-flex items-center gap-0.5 border border-border p-1 text-606060 transition-colors hover:border-primary/40 hover:text-primary'
						>
							<ArrowRightIcon className='size-3' />
							<CaretDownIcon className='size-2.5' />
						</button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align='start' onClick={e => e.stopPropagation()}>
						{forwardOptions.map(opt => (
							<DropdownMenuItem key={opt.id} onSelect={() => fireForward(opt)}>
								{opt.label}
							</DropdownMenuItem>
						))}
					</DropdownMenuContent>
				</DropdownMenu>
			)}
		</div>
	)
}

// --- kanban card ---

type WorkItemCardProps = {
	item: WorkItem
	step: Step
	phase: Phase
	onFireTransition: (item: WorkItem, transitionId: string) => Promise<unknown>
	onComplete: (item: WorkItem) => void
	onMoveBack: (item: WorkItem, stepId: string) => Promise<unknown>
}

const WorkItemCard = ({ item, step, phase, onFireTransition, onComplete, onMoveBack }: WorkItemCardProps) => {
	const c = useIntlayer('work-item').detail
	const openWorkItem = useOpenWorkItem()
	const { formatPreferenceDate } = useDateFormat()
	const nowMs = useSelector(boardNowMs$)
	const members = useSelector(members$)
	const allWorkItems = useSelector(workItems$)

	const isSpawn = !!item.parentId
	const parent = isSpawn ? (allWorkItems.find(w => w.id === item.parentId) ?? null) : null
	const assignee = members.find(m => m.id === item.assignedTo) ?? null
	const urgency = dueUrgency(item.dueDate, nowMs)
	const age = stepAgeDays(item, nowMs)

	return (
		<KanbanItem
			value={item.id}
			handle
			className={`group/card border bg-background transition-colors hover:border-primary/40 ${isSpawn ? 'border-l-2 border-l-primary/40 border-border' : item.status === 'awaiting_handoff' ? 'border-amber-300/60 dark:border-amber-700/60' : 'border-border'}`}
		>
			<div className='flex items-start gap-1 p-2.5'>
				<KanbanItemHandle className='-ml-1 mt-0.5 shrink-0 cursor-grab text-transparent transition-colors group-hover/card:text-606060 hover:text-foreground! active:cursor-grabbing'>
					<DotsSixVerticalIcon className='size-4' />
				</KanbanItemHandle>
				<div className='min-w-0 flex-1'>
					<button type='button' onClick={() => openWorkItem(item)} className='block w-full text-left'>
						{isSpawn && parent && (
							<p className='mb-1 truncate text-xxs font-medium text-primary/60'>↳ {parent.title}</p>
						)}
						<div className='flex items-start gap-1.5'>
							{item.priority && (
								<span className='mt-0.5 shrink-0'>
									<PriorityIndicator priority={item.priority} />
								</span>
							)}
							{item.size && (
								<span className='mt-0.5 shrink-0 bg-muted px-1 py-0.5 text-xxs font-medium text-606060'>
									{item.size}
								</span>
							)}
							{item.status === 'open' && item.workState === 'in_progress' && (
								<span
									className='mt-1.5 size-2 shrink-0 rounded-full bg-emerald-500'
									title={c.workStateInProgress.value}
								/>
							)}
							<p className='text-sm font-medium leading-snug'>{item.title}</p>
						</div>
						{item.status === 'awaiting_handoff' && (
							<span className='mt-1.5 inline-flex items-center bg-amber-100 px-1.5 py-0.5 text-xxs font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'>
								{c.awaitingAcceptance}
							</span>
						)}
						{item.subjectName && <p className='mt-1 text-xs text-606060'>{item.subjectName}</p>}
						{item.customerName && <p className='mt-0.5 text-xxs text-878787'>{item.customerName}</p>}
						{item.labels.length > 0 && (
							<div className='mt-1.5 flex flex-wrap gap-1'>
								{item.labels.map(label => (
									<span key={label} className='bg-hover-group px-1.5 py-0.5 text-xxs text-878787'>
										{label}
									</span>
								))}
							</div>
						)}
						<div className='mt-2.5 flex items-center gap-2 text-xxs text-606060'>
							<AssigneeAvatar name={assignee?.name ?? null} avatarUrl={assignee?.avatarUrl ?? null} />
							<span className='truncate'>{assignee?.name ?? c.cardUnassigned}</span>
							{item.comments.length > 0 && (
								<span className='inline-flex items-center gap-0.5'>
									<ChatCircleIcon className='size-3' />
									{item.comments.length}
								</span>
							)}
							{item.dueDate && (
								<span
									className={`ml-auto inline-flex items-center gap-1 ${
										urgency === 'overdue'
											? 'font-medium text-destructive'
											: urgency === 'soon'
												? 'font-medium text-amber-600 dark:text-amber-400'
												: ''
									}`}
								>
									<CalendarBlankIcon className='size-3' />
									{urgency === 'overdue' ? c.cardOverdue : formatPreferenceDate(item.dueDate)}
								</span>
							)}
							{age !== null && age > 0 && (
								<span className={item.dueDate ? '' : 'ml-auto'}>
									{age}
									{c.cardDaysSuffix}
								</span>
							)}
						</div>
					</button>
					{!isSpawn && <SpawnList parentId={item.id} />}
					<WorkItemCardActions
						item={item}
						step={step}
						phase={phase}
						onFireTransition={onFireTransition}
						onComplete={onComplete}
						onMoveBack={onMoveBack}
					/>
				</div>
			</div>
		</KanbanItem>
	)
}

// --- kanban step column ---

type KanbanStepColumnProps = {
	step: Step
	phase: Phase
	columns: PhaseColumns
	onFireTransition: (item: WorkItem, transitionId: string) => Promise<unknown>
	onComplete: (item: WorkItem) => void
	onMoveBack: (item: WorkItem, stepId: string) => Promise<unknown>
}

const KanbanStepColumn = ({
	step,
	phase,
	columns,
	onFireTransition,
	onComplete,
	onMoveBack,
}: KanbanStepColumnProps) => {
	const items = columns[step.id] ?? []
	const awaitingCount = items.filter(i => i.status === 'awaiting_handoff').length
	return (
		<KanbanColumn
			value={step.id}
			className={`flex snap-start min-h-45 flex-col gap-2 p-2 transition-colors data-[dragging=true]:border-primary/40 ${step.isInbox ? 'bg-amber-50/40 dark:bg-amber-950/20 border border-amber-200/60 dark:border-amber-800/40' : 'bg-muted/30'}`}
		>
			<div className='mb-0.5 flex items-center gap-2 px-1.5 pt-1'>
				<span className={`text-xs font-medium ${step.isInbox ? 'text-amber-700 dark:text-amber-400' : 'text-foreground/80'}`}>{step.name}</span>
				<span className='text-xxs tabular-nums text-606060'>{items.length}</span>
				{awaitingCount > 0 && (
					<span className='text-xxs tabular-nums font-medium text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/40 px-1 py-0.5 rounded'>{awaitingCount}</span>
				)}
				<KanbanColumnHandle className='ml-auto size-4 text-606060 hover:text-foreground' />
			</div>
			<KanbanColumnContent value={step.id}>
				{items.length === 0 && (
					<div className='border border-dashed border-border py-6 text-center text-xxs text-606060'>—</div>
				)}
				{items.map(item => (
					<WorkItemCard
						key={item.id}
						item={item}
						step={step}
						phase={phase}
						onFireTransition={onFireTransition}
						onComplete={onComplete}
						onMoveBack={onMoveBack}
					/>
				))}
			</KanbanColumnContent>
		</KanbanColumn>
	)
}

// --- drag overlay ---

type KanbanDragOverlayContentProps = {
	value: UniqueIdentifier
	variant: 'item' | 'column'
	phase: Phase
}

const KanbanDragOverlayContent = ({ value, variant, phase }: KanbanDragOverlayContentProps) => {
	const id = String(value)
	const allWorkItems = useSelector(workItems$)

	if (variant === 'item') {
		const item = allWorkItems.find(w => w.id === id)
		if (!item) return null
		const isSpawn = !!item.parentId
		const parent = isSpawn ? (allWorkItems.find(w => w.id === item.parentId) ?? null) : null
		return (
			<div
				className={`cursor-grabbing border bg-background p-2.5 shadow-md ${isSpawn ? 'border-l-2 border-l-primary/40 border-border' : 'border-border'}`}
			>
				{isSpawn && parent && <p className='mb-1 truncate text-xxs font-medium text-primary/60'>↳ {parent.title}</p>}
				<p className='text-sm font-medium leading-snug'>{item.title}</p>
				{item.subjectName && <p className='mt-1 text-xs text-606060'>{item.subjectName}</p>}
			</div>
		)
	}

	const step = phase.teamWorkflow.steps.find(s => s.id === id)
	return (
		<div className='min-h-45 w-72 border border-border bg-muted/40 p-2 opacity-90'>
			<span className='text-xs font-medium text-foreground/80'>{step?.name ?? id}</span>
		</div>
	)
}

// --- done column ---

type PhaseDoneColumnProps = { phaseId: string }

const PhaseDoneColumn = ({ phaseId }: PhaseDoneColumnProps) => {
	const c = useIntlayer('work-item').detail
	const openWorkItem = useOpenWorkItem()
	const allWorkItems = useSelector(workItems$)
	const [query, setQuery] = useState('')
	const ledger = phaseDoneLedger(phaseId, allWorkItems)
	const q = query.trim().toLowerCase()
	const filtered = q
		? ledger.filter(item => item.title.toLowerCase().includes(q) || item.subjectName.toLowerCase().includes(q))
		: ledger

	return (
		<div className='flex shrink-0 flex-col gap-2.5 p-3'>
			<div className='flex items-center gap-2 px-0.5'>
				<span className='text-xs font-medium text-foreground/80'>{c.doneColumnTitle}</span>
				<span className='text-xxs tabular-nums text-606060'>{ledger.length}</span>
			</div>
			<div className='relative'>
				<MagnifyingGlassIcon className='pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-606060' />
				<Input
					value={query}
					onChange={e => setQuery(e.target.value)}
					placeholder={c.doneSearchPlaceholder.value}
					className='h-7 pl-7 text-xs'
				/>
			</div>
			{filtered.length === 0 ? (
				<p className='px-0.5 py-2 text-xs text-606060'>{c.doneColumnEmpty}</p>
			) : (
				<div className='flex flex-col gap-2'>
					{filtered.map(item => (
						<button
							key={item.id}
							type='button'
							onClick={() => openWorkItem(item)}
							className='block w-full border border-border bg-background p-2.5 text-left transition-colors hover:border-primary/40'
						>
							<p className='text-sm font-medium leading-snug'>{item.title}</p>
							{item.subjectName && <p className='mt-1 text-xs text-606060'>{item.subjectName}</p>}
						</button>
					))}
				</div>
			)}
		</div>
	)
}

// --- phase kanban ---

type PhaseKanbanProps = {
	phase: Phase
	columns: PhaseColumns
	onColumnsChange: (columns: PhaseColumns) => void
	onMove: (event: KanbanMoveEvent) => void
	onFireTransition: (item: WorkItem, transitionId: string) => Promise<unknown>
	onMoveBack: (item: WorkItem, stepId: string) => Promise<unknown>
}

const PhaseKanban = ({ phase, columns, onColumnsChange, onMove, onFireTransition, onMoveBack }: PhaseKanbanProps) => {
	const { complete } = useWorkItemLifecycle([phase])
	const columnsWithDone = useMemo(() => ({ ...columns, [DONE_COLUMN_ID]: [] as WorkItem[] }), [columns])
	const savedOrder = useSelector(kanbanColumnOrder$)

	const orderedSteps = useMemo(() => {
		const order = savedOrder[phase.id]
		if (!order) return phase.teamWorkflow.steps
		const map = new Map(phase.teamWorkflow.steps.map(s => [s.id, s]))
		return order.map(id => map.get(id)).filter((s): s is Step => s !== undefined)
	}, [phase, savedOrder])

	const handleColumnsChange = useCallback(
		(cols: Record<string, WorkItem[]>) => {
			const filtered = Object.fromEntries(Object.entries(cols).filter(([k]) => k !== DONE_COLUMN_ID))
			kanbanColumnOrder$.set(prev => ({ ...prev, [phase.id]: Object.keys(filtered) }))
			onColumnsChange(filtered)
		},
		[phase.id, onColumnsChange],
	)

	const handleMove = useEffectEvent(async (event: KanbanMoveEvent) => {
		if (event.overContainer !== DONE_COLUMN_ID) return onMove(event)
		const item = (columns[event.activeContainer] ?? [])[event.activeIndex]
		if (!item) return
		const handoffs = findHandoffTransitions(phase, item.currentStepId)
		const defaultHandoff = handoffs.find(t => t.isDefault) ?? handoffs[0]
		if (defaultHandoff) onFireTransition(item, defaultHandoff.id)
		else complete(item)
	})

	return (
		<div className='flex flex-col gap-3'>
			<h2 className='text-xs font-medium uppercase tracking-wider text-606060'>{phase.name}</h2>
			<div className='overflow-x-auto snap-x scrollbar-none snap-mandatory pt-2 pb-5'>
				<div className='flex min-w-max items-start gap-3'>
					<Kanban
						value={columnsWithDone}
						onValueChange={handleColumnsChange}
						getItemValue={item => item.id}
						onMove={handleMove}
					>
						<KanbanBoard className='grid auto-cols-[288px] grid-flow-col gap-3'>
							{orderedSteps.map(step => (
								<KanbanStepColumn
									key={step.id}
									step={step}
									phase={phase}
									columns={columns}
									onFireTransition={onFireTransition}
									onComplete={complete}
									onMoveBack={onMoveBack}
								/>
							))}
							<KanbanColumn
								value={DONE_COLUMN_ID}
								className='border snap-start border-dashed border-border bg-muted/30 data-[dragging=true]:border-primary/40'
							>
								<PhaseDoneColumn phaseId={phase.id} />
							</KanbanColumn>
						</KanbanBoard>
						<KanbanOverlay>
							{({ value, variant }) => <KanbanDragOverlayContent value={value} variant={variant} phase={phase} />}
						</KanbanOverlay>
					</Kanban>
				</div>
			</div>
		</div>
	)
}

// --- archived section ---

const WorkflowArchivedSection = () => {
	const c = useIntlayer('work-item').detail
	const openWorkItem = useOpenWorkItem()
	const { formatPreferenceDate } = useDateFormat()
	const nowMs = useSelector(boardNowMs$)
	const members = useSelector(members$)
	const allWorkItems = useSelector(workItems$)
	const [query, setQuery] = useState('')

	const ledger = workflowArchivedLedger(allWorkItems, nowMs)
	if (ledger.length === 0) return null

	const q = query.trim().toLowerCase()
	const filtered = q
		? ledger.filter(item => item.title.toLowerCase().includes(q) || item.subjectName.toLowerCase().includes(q))
		: ledger

	const statusLabel = (status: WorkItem['status']): string => {
		const map: Record<WorkItem['status'], string> = {
			inbox: c.statusInbox.value,
			open: c.statusOpen.value,
			awaiting_handoff: c.statusAwaitingHandoff.value,
			completed: c.statusCompleted.value,
			cancelled: c.statusCancelled.value,
			archived: c.statusArchived.value,
		}
		return map[status]
	}

	return (
		<div className='flex flex-col gap-3'>
			<h2 className='text-xs font-medium uppercase tracking-wider text-606060'>
				{c.archivedColumnTitle}
				<span className='ml-2 tabular-nums text-606060'>{ledger.length}</span>
			</h2>
			<div className='flex flex-col gap-2 border border-dashed border-border bg-muted/20 p-3'>
				<div className='relative max-w-72'>
					<MagnifyingGlassIcon className='pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-606060' />
					<Input
						value={query}
						onChange={e => setQuery(e.target.value)}
						placeholder={c.archivedSearchPlaceholder.value}
						className='h-7 pl-7 text-xs'
					/>
				</div>
				{filtered.length === 0 ? (
					<p className='mt-1 text-xs text-606060'>{c.archivedColumnEmpty}</p>
				) : (
					<div className='grid gap-2 sm:grid-cols-[repeat(auto-fill,288px)]'>
						{filtered.map(item => {
							const assignee = members.find(m => m.id === item.assignedTo) ?? null
							return (
								<button
									key={item.id}
									type='button'
									onClick={() => openWorkItem(item)}
									className='block w-full border border-border bg-background p-2.5 text-left transition-colors hover:border-primary/40'
								>
									<div className='mb-1 flex flex-wrap items-center gap-1.5'>
										<span className='bg-muted px-1.5 py-0.5 text-xxs font-medium text-606060'>
											{statusLabel(item.status)}
										</span>
										{item.priority && <PriorityIndicator priority={item.priority} />}
									</div>
									<p className='text-sm font-medium leading-snug'>{item.title}</p>
									{item.subjectName && <p className='mt-1 text-xs text-606060'>{item.subjectName}</p>}
									{item.labels.length > 0 && (
										<div className='mt-1.5 flex flex-wrap gap-1'>
											{item.labels.map(label => (
												<span key={label} className='bg-hover-group px-1.5 py-0.5 text-xxs text-878787'>
													{label}
												</span>
											))}
										</div>
									)}
									<div className='mt-2 flex items-center gap-2 text-xxs text-606060'>
										<AssigneeAvatar name={assignee?.name ?? null} avatarUrl={assignee?.avatarUrl ?? null} />
										<span className='truncate'>{assignee?.name ?? c.cardUnassigned}</span>
										{item.dueDate && (
											<span className='ml-auto inline-flex items-center gap-1'>
												<CalendarBlankIcon className='size-3' />
												{formatPreferenceDate(item.dueDate)}
											</span>
										)}
									</div>
								</button>
							)
						})}
					</div>
				)}
			</div>
		</div>
	)
}

// --- workflow kanban ---

type WorkflowKanbanProps = {
	workflow: CompanyWorkflow
}

const WorkflowKanban = ({ workflow }: WorkflowKanbanProps) => {
	const { allColumns, setPhaseColumns } = useKanbanColumns(workflow)
	const { onMove, fire, moveBack } = useFireTransition(workflow)
	const phases = visiblePhases(workflow.phases, getWorkItemActor())

	// Shared board clock: tick a single "now" the cards derive due/age urgency
	// from. An effect (not a render-time write) keeps it off the render path so
	// it never schedules a subscriber update mid-render. A coarse interval keeps
	// urgency fresh on long-open boards without re-rendering every frame.
	useEffect(() => {
		boardNowMs$.set(Date.now())
		const id = setInterval(() => boardNowMs$.set(Date.now()), 60_000)
		return () => clearInterval(id)
	}, [])

	return (
		<>
			{phases.map(phase => (
				<PhaseKanban
					key={phase.id}
					phase={phase}
					columns={allColumns[phase.id] ?? {}}
					onColumnsChange={cols => setPhaseColumns(phase.id, cols)}
					onMove={onMove}
					onFireTransition={fire}
					onMoveBack={moveBack}
				/>
			))}
			<WorkflowArchivedSection />
		</>
	)
}

// --- page ---

const WorkflowPage = ({ workflowId }: Props) => {
	const { workflows } = useWorkflows()
	useWorkItems(workflowId)
	useAllMembers()
	const c = useIntlayer('work-item').create
	const workflow = workflows.find(w => w.id === workflowId) ?? null

	if (!workflow) return null

	return (
		<div className='overflow-hidden'>
			<div className='flex items-center justify-end gap-4'>
				<Button onClick={() => creatingWorkItem$.set(true)}>{c.newButton}</Button>
			</div>
			<WorkflowKanban workflow={workflow} />
			<WorkItemCreateSheet workflow={workflow} />
		</div>
	)
}

export { WorkflowPage }
